import { useRef, useState } from "react";
import { useTranslate, type Identifier } from "ra-core";
import { CheckCircle2, MinusCircle, XCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { APP_LOCALE, NUMERIC_DATE_FORMAT } from "../../misc/locale";
import {
  DOC_TYPE_LABELS,
  EXPEDIENTE_ALLOWED_MIME_TYPES,
  PEP_DOC_TYPE,
  REQUIRED_DOC_TYPES_BY_PERSON_KIND,
  validateExpedienteDocumentFile,
} from "../../providers/commons/contactExpediente";
import { ReasonDialog } from "../../operations/OperationDocumentDialogs";
import type {
  ContactExpedienteDocType,
  ContactExpedienteDocument,
  ExpedientePersonKind,
} from "../../types";

type DocumentActions = {
  upload: (docType: ContactExpedienteDocType, file: File) => Promise<void>;
  remove: (documentId: Identifier, reason: string) => Promise<void>;
  getSignedUrl: (documentId: Identifier) => Promise<string>;
};

const errorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : "No se pudo completar la acción";

/** The current (not removed) version of a document type, if any. */
const currentDocument = (
  documents: ContactExpedienteDocument[],
  docType: ContactExpedienteDocType,
) =>
  documents
    .filter((doc) => doc.doc_type === docType && !doc.deleted_at)
    .sort((a, b) => b.version_number - a.version_number)[0];

const DocumentBlock = ({
  docType,
  required,
  pepRequired,
  document,
  actions,
}: {
  docType: ContactExpedienteDocType;
  required: boolean;
  pepRequired: boolean;
  document: ContactExpedienteDocument | undefined;
  actions: DocumentActions;
}) => {
  const translate = useTranslate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);

  const run = async (task: () => Promise<void>) => {
    setBusy(true);
    setNotice(null);
    try {
      await task();
    } catch (error) {
      setNotice(errorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const onFile = (file: File | undefined) => {
    if (!file) return;
    const invalid = validateExpedienteDocumentFile(file);
    if (invalid) {
      setNotice(invalid);
      return;
    }
    void run(() => actions.upload(docType, file));
  };

  const view = (current: ContactExpedienteDocument) => {
    // Open the tab synchronously with the click so popup blockers (Safari/iOS) allow it.
    const tab = window.open("", "_blank");
    if (tab) tab.opener = null;
    return run(async () => {
      try {
        const url = await actions.getSignedUrl(current.id);
        if (tab) tab.location.href = url;
      } catch (error) {
        tab?.close();
        throw error;
      }
    });
  };

  const status = document ? "uploaded" : required ? "missing" : "optional";
  const StatusIcon =
    status === "uploaded"
      ? CheckCircle2
      : status === "missing"
        ? XCircle
        : MinusCircle;

  return (
    <li className="flex flex-col gap-2 rounded-md border p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-[15px] font-semibold">
          {DOC_TYPE_LABELS[docType]}
        </h4>
        <span
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium",
            status === "uploaded" && "bg-brand-green-bg text-brand-green-text",
            status === "missing" && "bg-brand-red-bg text-brand-red-text",
            status === "optional" && "bg-muted text-muted-foreground",
          )}
        >
          <StatusIcon className="size-4" aria-hidden="true" />
          {translate(`crm.contact_expediente.doc_${status}`)}
        </span>
      </div>
      <p className="text-[15px] text-muted-foreground">
        {translate("crm.contact_expediente.doc_by")}
        {pepRequired
          ? `. ${translate("crm.contact_expediente.doc_pep_required")}`
          : ""}
      </p>
      {document ? (
        <p className="text-[15px] tabular-nums">
          {document.file_name} ·{" "}
          {new Date(document.uploaded_at).toLocaleDateString(
            APP_LOCALE,
            NUMERIC_DATE_FORMAT,
          )}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <input
          ref={inputRef}
          type="file"
          className="sr-only"
          tabIndex={-1}
          aria-label={DOC_TYPE_LABELS[docType]}
          accept={EXPEDIENTE_ALLOWED_MIME_TYPES.join(",")}
          onChange={(event) => {
            onFile(event.target.files?.[0]);
            event.target.value = "";
          }}
        />
        <Button
          type="button"
          variant="outline"
          className="h-11 sm:h-9"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
        >
          {translate(
            document
              ? "crm.contact_expediente.doc_replace"
              : "crm.contact_expediente.doc_upload",
          )}
        </Button>
        {document ? (
          <>
            <Button
              type="button"
              variant="outline"
              className="h-11 sm:h-9"
              disabled={busy}
              onClick={() => void view(document)}
            >
              {translate("crm.contact_expediente.doc_view")}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="h-11 sm:h-9"
              disabled={busy}
              onClick={() => setRemoving(true)}
            >
              {translate("crm.contact_expediente.doc_remove")}
            </Button>
          </>
        ) : null}
      </div>
      {notice ? (
        <p role="status" className="text-[15px]">
          {notice}
        </p>
      ) : null}
      {removing && document ? (
        <ReasonDialog
          title={translate("crm.contact_expediente.doc_remove_title")}
          description={translate("crm.contact_expediente.doc_remove_hint")}
          onClose={() => setRemoving(false)}
          onSubmit={async (reason) => {
            try {
              await actions.remove(document.id, reason);
              setRemoving(false);
              return null;
            } catch (error) {
              return errorMessage(error);
            }
          }}
        />
      ) : null}
    </li>
  );
};

/**
 * The documents block: one entry per required document type of the person kind (plus the PLD
 * support document, required only for a PEP) with its subido/falta semaforo (icon plus text),
 * who uploads it, upload to the private bucket, "Ver" through a short-lived signed URL, and
 * soft removal that asks for a reason. In the demo "Ver" answers that the file is not available
 * and only the metadata is kept.
 */
export const ExpedienteDocuments = ({
  personKind,
  isPep,
  documents,
  actions,
}: {
  personKind: ExpedientePersonKind | null | undefined;
  isPep: boolean | null | undefined;
  documents: ContactExpedienteDocument[];
  actions: DocumentActions;
}) => {
  const translate = useTranslate();
  const required =
    REQUIRED_DOC_TYPES_BY_PERSON_KIND[personKind ?? "persona_fisica"];
  const types: ContactExpedienteDocType[] = [...required, PEP_DOC_TYPE];
  return (
    <section className="flex flex-col gap-4">
      <h3 className="text-lg font-semibold">
        {translate("crm.contact_expediente.section_docs")}
      </h3>
      <ul className="flex flex-col gap-3">
        {types.map((docType) => (
          <DocumentBlock
            key={docType}
            docType={docType}
            required={docType === PEP_DOC_TYPE ? isPep === true : true}
            pepRequired={docType === PEP_DOC_TYPE && isPep === true}
            document={currentDocument(documents, docType)}
            actions={actions}
          />
        ))}
      </ul>
    </section>
  );
};
